import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { MAX_NAME_LENGTH } from '~/consts';
import { Icon } from '~/modules/elements/akui/icon';
import events from '~/modules/game-events/game-events';
import { useEventListener } from '~/modules/game-events/hooks';
import { LOCAL_PLAYER_NUMBERS, PlayerNumber } from '~/modules/players/player-number';
import { serverRpc } from '~/modules/remote-mic/network/client';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import { profileInitial } from '~/modules/user-profiles/profiles';
import ColourTiles from '~/routes/remote-mic/components/colour-tiles';
import { checkSingerName } from '~/routes/remote-mic/components/name-rule';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';
import { playerColour, playerColourName, playerInk } from '~/routes/remote-mic/components/player-colour';
import useColourOccupants from '~/routes/remote-mic/components/use-colour-occupants';
import usePermissions from '~/routes/remote-mic/hooks/use-permissions';
import useRemoteMicId from '~/routes/remote-mic/hooks/use-remote-mic-id';

interface Props {
  // The name this phone already answers to, when it remembers one - it starts on the colour screen.
  rememberedName: string | null;
  onName: (name: string) => void;
  onComplete: () => void;
}

type Stage = 'who' | 'name' | 'colour';

// Past this many singers the grid is longer than a screen, so a Find box appears above it.
const FIND_THRESHOLD = 8;
// Long enough to see the tile take the name before the microphone tab replaces it.
const JOINED_BEAT_MS = 600;

export default function StepSingerAndColour({ rememberedName, onName, onComplete }: Props) {
  const singers = useSubscription('singers') ?? [];
  const permissions = usePermissions();
  const myMicId = useRemoteMicId();
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];

  // Held as an override so the opening screen can follow the singer list, which arrives over the
  // wire after this mounts: an empty room starts on the name rather than on an empty grid.
  const [stageOverride, setStageOverride] = useState<Stage | null>(rememberedName ? 'colour' : null);
  const stage = stageOverride ?? (singers.length === 0 ? 'name' : 'who');
  const [pick, setPick] = useState<string | null>(rememberedName);
  const [draft, setDraft] = useState('');
  // Taken as the step opens: naming the singer here stores the name, which would otherwise make every
  // phone a returning one by the time it reaches its colour.
  const [returningName] = useState(rememberedName);

  const heldElsewhere = (heldByMicId: string | null) => heldByMicId !== null && heldByMicId !== myMicId;

  const choose = (name: string) => {
    onName(name);
    setPick(name);
    // A read-only phone is never scored, so it has no colour to pick; one the television already
    // seated has its colour.
    if (permissions !== 'write' || playerNumber !== null) {
      onComplete();
      return;
    }
    setStageOverride('colour');
  };

  const toName = (carried: string) => {
    setDraft(carried);
    setStageOverride('name');
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {stage === 'who' && (
        <WhoIsSinging
          singers={singers.map((singer) => ({ ...singer, taken: heldElsewhere(singer.heldByMicId) }))}
          onPick={choose}
          onNew={toName}
        />
      )}
      {stage === 'name' && (
        <WhatsYourName
          draft={draft}
          // Typing pins this screen: a singer list arriving mid-word would otherwise swap it for
          // Who's singing.
          setDraft={(value) => {
            setDraft(value);
            setStageOverride('name');
          }}
          singers={singers.map((singer) => ({ name: singer.name, taken: heldElsewhere(singer.heldByMicId) }))}
          onBack={singers.length > 0 ? () => setStageOverride('who') : undefined}
          onSubmit={choose}
        />
      )}
      {stage === 'colour' && (
        <PickYourColour
          name={pick ?? ''}
          returning={!!returningName && pick === returningName}
          onChange={() => setStageOverride(singers.length > 0 ? 'who' : 'name')}
          onComplete={onComplete}
        />
      )}
    </div>
  );
}

function WhoIsSinging({
  singers,
  onPick,
  onNew,
}: {
  singers: Array<{ name: string; taken: boolean }>;
  onPick: (name: string) => void;
  onNew: (carried: string) => void;
}) {
  const flash = usePhoneToast();
  const [query, setQuery] = useState('');
  const showFind = singers.length > FIND_THRESHOLD;
  const wanted = query.trim().toLowerCase();
  const shown = showFind && wanted ? singers.filter((singer) => singer.name.toLowerCase().includes(wanted)) : singers;

  return (
    <>
      <span className="mt-4.5 shrink-0 text-[1.875rem] leading-[1.1] font-bold">Who&#39;s singing?</span>
      {showFind && (
        <div
          className={twMerge(
            'mt-4.5 flex h-11 shrink-0 items-center gap-2.5 rounded-[0.875rem] bg-white/8 px-3.5',
            wanted ? 'shadow-[inset_0_0_0_2px_#facc15]' : 'shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]',
          )}>
          <Icon icon="ic:baseline-search" size={5} className="shrink-0 text-white/60" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Find your name · ${singers.length} singers`}
            data-test="singer-find-input"
            className="ph-no-capture h-full min-w-0 flex-1 border-none bg-transparent text-[1rem] text-white outline-none placeholder:text-white/45"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => setQuery('')}
              className="flex shrink-0 cursor-pointer border-none bg-transparent p-0 text-white">
              <Icon icon="ic:baseline-close" size={5} />
            </button>
          )}
        </div>
      )}
      <div className="-mx-5 mt-5.5 min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-1">
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-5.5">
          <button
            type="button"
            onClick={() => onNew(query.trim())}
            data-test="new-singer"
            className="flex cursor-pointer flex-col items-center gap-2.5 border-none bg-transparent p-0 text-white">
            <span className="flex size-22 items-center justify-center rounded-full shadow-[inset_0_0_0_2px_rgba(255,255,255,0.35)]">
              <Icon icon="ic:baseline-add" size={9} />
            </span>
            <span className="flex flex-col items-center">
              <span className="text-[1rem] font-bold">New</span>
              <span className="text-[0.75rem] text-white/70">{singers.length === 0 ? 'First singer' : ''}</span>
            </span>
          </button>
          {shown.map((singer) => (
            <button
              key={singer.name}
              type="button"
              onClick={() => (singer.taken ? flash(`${singer.name} is singing on another phone`) : onPick(singer.name))}
              data-test={`singer-${singer.name.toLowerCase()}`}
              data-taken={singer.taken}
              className="flex min-w-0 cursor-pointer flex-col items-center gap-2.5 border-none bg-transparent p-0 text-white"
              style={{ opacity: singer.taken ? 0.5 : 1 }}>
              <span className="relative flex size-22 items-center justify-center rounded-full bg-white/12 text-[2.125rem] font-bold">
                {profileInitial(singer.name)}
                {singer.taken && (
                  <span className="absolute -right-0.5 -bottom-0.5 flex size-7 items-center justify-center rounded-full bg-[#020617]">
                    <Icon icon="ic:outline-lock" size={4} />
                  </span>
                )}
              </span>
              <span className="flex w-full min-w-0 flex-col items-center">
                <span className="ph-no-capture max-w-full truncate text-[1rem] font-bold">{singer.name}</span>
                <span className="text-[0.75rem] text-white/70">{singer.taken ? 'Other phone' : ''}</span>
              </span>
            </button>
          ))}
        </div>
        {showFind && wanted && shown.length === 0 && (
          <span
            data-test="singer-no-match"
            className="mt-2 block text-center text-[0.9375rem] leading-[1.45] text-white/75">
            No singer called &quot;{query.trim()}&quot;. Tap New to add yourself.
          </span>
        )}
      </div>
    </>
  );
}

function WhatsYourName({
  draft,
  setDraft,
  singers,
  onBack,
  onSubmit,
}: {
  draft: string;
  setDraft: (draft: string) => void;
  singers: Array<{ name: string; taken: boolean }>;
  onBack?: () => void;
  onSubmit: (name: string) => void;
}) {
  const check = checkSingerName(draft, singers);
  const ready = check.status === 'existing' || check.status === 'new';

  const submit = () => {
    if (check.status === 'existing') onSubmit(check.match.name);
    if (check.status === 'new') onSubmit(check.name);
  };

  const label =
    check.status === 'empty'
      ? 'Type a name'
      : check.status === 'taken'
        ? 'Name taken'
        : check.status === 'existing'
          ? `Use ${check.match.name}`
          : 'Next';

  return (
    <>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          data-test="back-to-singers"
          className="mt-3.5 flex h-9 shrink-0 cursor-pointer items-center gap-1 self-start border-none bg-transparent pr-2.5 pl-1 text-[0.9375rem] text-white/85">
          <Icon icon="ic:baseline-arrow-back" size={5} />
          Singers
        </button>
      )}
      <span className="mt-4.5 shrink-0 text-[1.875rem] leading-[1.1] font-bold">What&#39;s your name?</span>
      <div className="mt-5.5 flex h-18 shrink-0 items-center rounded-[1.125rem] bg-black/55 px-4.5 shadow-[inset_0_0_0_2px_#facc15]">
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
          maxLength={MAX_NAME_LENGTH}
          placeholder="Enter your name…"
          autoFocus
          data-test="player-name-input"
          className="ph-no-capture h-full min-w-0 flex-1 border-none bg-transparent text-[1.5rem] font-bold text-white outline-none placeholder:text-white/45"
        />
      </div>
      {/* Held open whether or not there is a notice, so the button below never moves under a thumb. */}
      <div className="mt-3 min-h-18 shrink-0">
        {(check.status === 'existing' || check.status === 'taken') && (
          <div
            data-test="singer-name-clash"
            className="flex items-center gap-3 rounded-[0.875rem] bg-[rgba(255,165,0,0.14)] px-4 py-3.5 shadow-[inset_0_0_0_1px_rgba(255,165,0,0.5)]">
            <Icon icon="ic:baseline-info" size={5.5} className="text-active shrink-0" />
            <span className="text-[0.875rem] leading-[1.4]">
              {check.status === 'taken'
                ? `${check.match.name} is already singing on another phone. Pick a different name.`
                : 'That name is already a singer here. Pick them from the list so the scores land in one place.'}
            </span>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={!ready}
        data-test="confirm-name-button"
        className={twMerge(
          'ph-no-capture mt-auto flex h-16 shrink-0 cursor-pointer items-center justify-center rounded-[1.125rem] border-none px-4 text-[1.1875rem] font-bold transition-[background-color,transform] duration-200 active:scale-[0.98]',
          ready ? 'bg-active text-black' : 'bg-white/14 text-white/70',
        )}>
        <span className="truncate">{label}</span>
      </button>
    </>
  );
}

function PickYourColour({
  name,
  returning,
  onChange,
  onComplete,
}: {
  name: string;
  returning: boolean;
  onChange: () => void;
  onComplete: () => void;
}) {
  const flash = usePhoneToast();
  const micId = useRemoteMicId();
  const occupants = useColourOccupants(micId);
  const [picked, setPicked] = useState<PlayerNumber | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  /** Bumped by every tap and on unmount, so only the latest tap's answer acts. */
  const latestTap = useRef(0);
  const [playerNumber] = useEventListener(events.remoteMicPlayerSet, true) ?? [null];

  useEffect(
    () => () => {
      latestTap.current++;
      clearTimeout(timer.current);
    },
    [],
  );

  // A reconnect the television seats back on its old slot a moment after this opened already has its
  // colour, so it goes on rather than being asked for one.
  const seatedMeanwhile = picked === null && playerNumber !== null && playerNumber !== undefined;
  useEffect(() => {
    if (seatedMeanwhile) onComplete();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [seatedMeanwhile]);

  const allTaken = LOCAL_PLAYER_NUMBERS.every((number) => occupants[number] !== undefined);

  // No confirm: the colour can be changed afterwards, so a tap on a free one joins.
  const join = (number: PlayerNumber) => {
    if (!micId) return;
    setPicked(number);
    clearTimeout(timer.current);
    const tap = ++latestTap.current;
    // Joined only once the TV has seated it: a refused seat leaves the phone picking again.
    serverRpc.players.requestMicSelect(micId, number).then(
      () => {
        if (tap !== latestTap.current) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(onComplete, JOINED_BEAT_MS);
      },
      () => {
        if (tap !== latestTap.current) return;
        setPicked(null);
        flash('The TV did not take the colour');
      },
    );
  };

  const title = allTaken
    ? 'All colours are taken'
    : returning && picked === null
      ? 'Welcome back. Pick your colour'
      : 'Pick your colour';

  return (
    <>
      <button
        type="button"
        onClick={onChange}
        data-test="change-singer"
        className="mt-4 flex h-16 shrink-0 cursor-pointer items-center gap-3 rounded-[1.125rem] border-none bg-black/50 pr-3.5 pl-2.5 text-left text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]">
        <span
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-[1.1875rem] font-bold"
          style={
            picked === null
              ? { background: 'rgb(255,165,0)', color: '#000' }
              : { background: playerColour(picked), color: playerInk(picked) }
          }>
          {profileInitial(name)}
        </span>
        <span className="ph-no-capture min-w-0 flex-1 truncate text-[1.125rem] font-bold">{name}</span>
        <span className="shrink-0 text-[0.875rem] text-white/80">Change</span>
      </button>
      <span data-test="colour-title" className="mt-5.5 shrink-0 text-[1.875rem] leading-[1.1] font-bold">
        {title}
      </span>
      <div className="mt-5 shrink-0">
        <ColourTiles
          playerNumbers={LOCAL_PLAYER_NUMBERS}
          selected={picked}
          ownName={name}
          occupants={occupants}
          onPick={join}
          onBlocked={(number, occupant) => flash(`${occupant} has ${playerColourName(number)}`)}
        />
      </div>
      {allTaken ? (
        <button
          type="button"
          onClick={onComplete}
          data-test="join-without-colour"
          className="bg-active mt-auto flex h-16 shrink-0 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
          Join without a colour
        </button>
      ) : (
        // Joins as a remote that is not scored; a slot can still be taken on Setup microphones.
        <button
          type="button"
          onClick={onComplete}
          data-test="colour-not-now"
          className="mt-auto flex h-13 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-[0.9375rem] text-white/85">
          Not now
        </button>
      )}
    </>
  );
}
